import * as React from 'react';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { Grid } from '@material-ui/core';
import { getBackOrderInfo } from 'api/backorder';
import InformationTable from 'components/common/table/InformationTable';
import { InformationObject } from 'types/common/informationTable';
import { BackOrderInfo } from 'types/api/backorder';
import CurrentPlanTable from 'components/backorder/table/currentPlanTable/CurrentPlanTable';
import { getUtcUsFormatDate } from 'modules/utils/time';
import BackOrderSubscribe from 'components/backorder/BackOrderSubscribe';
import styled from '@emotion/styled';
import CurrentCreditBalance from 'components/backorder/table/currentPlanTable/CurrentCreditBalance';
import BackOrderList from 'components/backorder/BackOrderList';
import BackOrderCommentList from 'components/backorder/BackOrderCommentList';
import { RouteText } from 'components/plansales/billing/billingtable/DataTableCellRenderer';
import LinkButton from 'components/common/LinkButton';

interface Props {
  groupId: string;
}
const BackOrderContainer = ({ groupId }: Props): ReactElement | null => {
  const [backOrderInfo, setBackOrderInfo] = useState<BackOrderInfo | undefined>(undefined);
  const [backOrderInfoTableData, setBackOrderInfoTableData] = useState<InformationObject[]>([]);

  const fetchAndSetBackOrderInfo = useCallback(async () => {
    const res = await getBackOrderInfo({ groupId });
    setBackOrderInfo(res);
    if (!res) return;
    setCurrentPlanTableData(res);
  }, [groupId]);

  const setCurrentPlanTableData = useCallback(
    (data: BackOrderInfo) => {
      const { group, currentPlan, measurementPeriod, currentCreditBalance } = data;
      const tableData: InformationObject[] = [
        { title: 'User ID', render: group.groupAdminUserId },
        {
          title: 'Group ID',
          render: (
            <RouteText to={`/groups/${group.groupId}`} target="_blank" rel="noreferrer noopener">
              {group.groupId}
            </RouteText>
          ),
        },
        { title: 'Group Name', render: group.groupName },
        { title: 'Admin Email', render: group.groupAdminEmail },
        {
          title: 'Current Plan',
          render: (
            <CurrentPlanTable currentPlan={currentPlan} measurementPeriod={measurementPeriod} />
          ),
        },
        {
          title: 'Measurement Period',
          render: `${getUtcUsFormatDate(measurementPeriod.startDate)}  ~  ${getUtcUsFormatDate(
            measurementPeriod.endDate ?? '',
          )}`,
        },
        {
          title: 'Current Credit Balance',
          render: (
            <CurrentCreditBalance groupId={groupId} currentCreditBalance={currentCreditBalance} />
          ),
        },
      ];
      setBackOrderInfoTableData(tableData);
    },
    [backOrderInfo, setBackOrderInfo],
  );

  useEffect(() => {
    void fetchAndSetBackOrderInfo();
  }, []);

  if (!backOrderInfo) return null;
  return (
    <Grid>
      <ButtonWrapper>
        <LinkButton
          url={`/groups/${backOrderInfo.group.groupId}`}
          variant="contained"
          size="medium"
          color="primary"
          openNewTab
        >
          Group Detail
        </LinkButton>
      </ButtonWrapper>
      <InformationTable title={'Back Order Information'} data={backOrderInfoTableData} />
      <Gap gap={10} />
      <BackOrderSubscribe
        groupId={backOrderInfo.group.groupId}
        currentPlan={backOrderInfo.currentPlan}
      />
      <Gap gap={20} />
      <BackOrderList groupId={backOrderInfo.group.groupId} />
      <Gap gap={20} />
      <BackOrderCommentList groupId={backOrderInfo.group.groupId} />
    </Grid>
  );
};

export default BackOrderContainer;

const Gap = styled.div<{ gap: number }>`
  height: ${({ gap }) => gap}px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: end;
  padding-bottom: 10px;
`;
